import { useMemo } from 'react';

import {
  Box,
  Lightbox as BricksNextLightbox,
  Button,
  FavoriteButton,
  Text,
} from '@opendoor/bricks-next';
import {
  buildLightboxPhotoUrls,
  formatStreetAddress,
  isBuyDirectEligible,
} from 'components/marketplace/helpers';
import type { PropertyDetailsType } from 'components/marketplace/property';
import { useTouring } from 'components/self-tour/hooks/useTouring';
import { formatCurrency } from 'helpers/formatters';

import { SelfTourAppHandoffModal } from './PropertySection/SelfTourAppHandoffModal';
import { TourWithAgentModal } from './PropertySection/TourWithAgentModal';

interface LightboxProps {
  handleClose: () => void;
  isOpen: boolean;
  property: PropertyDetailsType;
  selectedPhotoIndex: number;
}

export const Lightbox = ({ handleClose, isOpen, property, selectedPhotoIndex }: LightboxProps) => {
  const formattedStreet = formatStreetAddress(
    property.street,
    property.unitType,
    property.unitNumber,
  );
  const lightboxPhotoUrls = useMemo(() => buildLightboxPhotoUrls(property), [property]);
  let displayPrice = 'Price not available';
  if (
    isBuyDirectEligible(property) &&
    property.displayBadge === 'ON_THE_MARKET' &&
    property.buyDirectPrice
  ) {
    displayPrice = formatCurrency(property.buyDirectPrice, { inDollars: true });
  } else if (property?.listPrice) {
    displayPrice = formatCurrency(property.listPrice, { inDollars: true });
  }

  const LightboxHeader = useMemo(() => {
    return (
      <Box flexDirection="column" alignItems="center" flex={1}>
        <Text tag="span" typography="$labelMedium" color="$contentInversePrimary">
          {displayPrice}
        </Text>
        <Text tag="span" typography="$bodyXsmall" color="$contentInversePrimary" ml="$2x">
          {formattedStreet}
        </Text>
      </Box>
    );
  }, [property]);

  const LightboxFooter = useMemo(() => {
    return (
      <Box
        flexDirection="row"
        justifyContent="space-between"
        pt="$4x"
        px="$12x"
        $smallerThanMD={{ display: 'none' }}
        width="100%"
      >
        <Box flexDirection="row" alignItems="center" flex={1}>
          <Text tag="span" typography="$labelMedium" color="$contentInversePrimary">
            {displayPrice}
          </Text>
          <Text tag="span" typography="$bodyMedium" color="$contentInversePrimary" ml="$2x">
            — {formattedStreet}, {property.city}, {property.state} {property.zip}
          </Text>
        </Box>
        <Box alignSelf="flex-end" alignItems="center" justifyContent="flex-end" flexDirection="row">
          <FavoriteButton
            analyticsName="cosmos-marketplace-pdp-lightbox-favorite-property-button"
            onPress={property?.onClickReaction}
            size="medium"
            variant="inverse"
            mx="$12x"
            isFavorited={property?.isLiked ?? false}
          />
          <TourButton property={property} />
        </Box>
      </Box>
    );
  }, [property]);

  return (
    <BricksNextLightbox
      analyticsName="cosmos-marketplace-property-photo-lightbox"
      footer={LightboxFooter}
      handleClose={handleClose}
      isOpen={isOpen}
      mobileTitle={LightboxHeader}
      photoUrls={lightboxPhotoUrls}
      selectedPhotoIndex={selectedPhotoIndex}
    />
  );
};

interface TourButtonProps {
  property: PropertyDetailsType;
}
const TourButton: React.FC<TourButtonProps> = ({ property }) => {
  const touringInfo = useTouring(property, { placement: 'lightbox' });
  if (!touringInfo) return null;

  const {
    primaryAction,
    secondaryAction,
    tourKind,
    closeDialog,
    isDialogOpen,
    isSelfTourDialogOpen,
    closeSelfTourDialog,
  } = touringInfo;

  return (
    <>
      {secondaryAction && (
        <Button
          analyticsName={secondaryAction.analyticsName}
          label={secondaryAction.text}
          onPress={secondaryAction.onClick}
          href={secondaryAction.href}
          target={secondaryAction.target}
          size="small"
          variant={secondaryAction.variant}
          disabled={secondaryAction.disabled}
          mr="$2x"
        />
      )}
      <Button
        analyticsName={primaryAction.analyticsName}
        label={primaryAction.text}
        onPress={primaryAction.onClick}
        href={primaryAction.href}
        target={primaryAction.target}
        size="small"
        variant={primaryAction.variant}
        disabled={primaryAction.disabled}
      />
      {tourKind === 'tourWithAgent' && (
        <TourWithAgentModal property={property} isOpen={isDialogOpen} onClose={closeDialog} />
      )}
      <SelfTourAppHandoffModal
        isOpen={isSelfTourDialogOpen}
        onClose={closeSelfTourDialog}
        placement="lightbox"
        addressToken={property.addressToken}
      />
    </>
  );
};
